import { useMemo, useState } from 'react'
import { type SortingState } from '@tanstack/react-table'
import {
    ActiveManagementFiltersValues,
    INTERVAL_MAX_METERS_DISPLAY_COUNT,
} from '@/utils/constants'

import type {
    IntervalDataEntry,
    IntervalReportSettings,
} from '@/hooks/useApi/useActiveManagementApi/types'
import { detectDataGrouping } from '../IntervalDataChart/detectDataGrouping'
import { useActiveManagementMetersQuery } from '@/features/ActiveManagement/hooks/useActiveManagementMetersQuery'
import { useActiveManagementSubmetersQuery } from '@/features/ActiveManagement/hooks/useActiveManagementSubmetersQuery'
import { getEntryId } from '@/features/ActiveManagement/utils/getEntryId'
import { useColumns } from './useColumns'
import { IntervalTable } from '../IntervalTable/IntervalTable'

type IntervalTableRow = {
    timestamp: string
    value?: number
    [meterId: string]: unknown
}

type IntervalTableContainerProps = {
    activeFilters: ActiveManagementFiltersValues
    reportSettings: IntervalReportSettings
    data: IntervalDataEntry[]
}

const getValueForEntry = (
    entry: IntervalDataEntry,
    dataType: IntervalReportSettings['dataType']
): number | undefined => {
    switch (dataType) {
        case 'usage':
            return entry.usage
        case 'demandAverage':
            return entry.demandAverage
        case 'demandMax':
            return entry.demandMax
        default:
            return entry.usage
    }
}

export const IntervalTableContainer: React.FC<IntervalTableContainerProps> = ({
    data,
    reportSettings,
    activeFilters,
}) => {
    const { data: meters } = useActiveManagementMetersQuery({ activeFilters })
    const { data: subMeters } = useActiveManagementSubmetersQuery({
        activeFilters,
    })

    const { display } = reportSettings

    const selectedMeters = useMemo(() => {
        return display === 'subMeters'
            ? (activeFilters.subMeter ?? [])
            : activeFilters.meter
    }, [display, activeFilters.meter, activeFilters.subMeter])

    const allMeters = useMemo(() => {
        return display === 'subMeters' ? subMeters : meters
    }, [display, meters, subMeters])

    const dataGrouping = useMemo(
        () => detectDataGrouping(data, reportSettings.display),
        [data, reportSettings.display]
    )

    const meterNameById = useMemo(() => {
        const map = new Map<number, string>()
        for (const meter of allMeters ?? []) {
            map.set(meter.meterId, meter.meterName)
        }
        return map
    }, [allMeters])

    const meterIdsToShow = useMemo(() => {
        const ids = Array.from(
            new Set(
                data
                    .map((d) => getEntryId(d, display))
                    .filter((id): id is number => typeof id === 'number')
            )
        ).sort((a, b) => a - b)

        return ids.slice(0, INTERVAL_MAX_METERS_DISPLAY_COUNT)
    }, [data, display])

    const metersCount = useMemo(() => {
        return selectedMeters.length > 0
            ? selectedMeters.length
            : (allMeters?.length ?? 0)
    }, [allMeters?.length, selectedMeters.length])

    const aggregatedMetersCount = useMemo(() => {
        // In compare mode we only show a limited number of meters on screen
        if (reportSettings.dataGrouping === 'compare') {
            return Math.min(metersCount, INTERVAL_MAX_METERS_DISPLAY_COUNT)
        }
        return metersCount
    }, [metersCount, reportSettings.dataGrouping])

    const columns = useColumns({
        dataGrouping,
        reportSettings,
        aggregatedMetersCount,
        meterIdsToShow,
        meterNameById,
    })

    const rows = useMemo<IntervalTableRow[]>(() => {
        if (dataGrouping === 'aggregate') {
            const map = new Map<string, number>()
            for (const entry of data) {
                const ts = entry.date
                const value = getValueForEntry(entry, reportSettings.dataType)
                if (!ts || value === undefined) continue
                map.set(ts, (map.get(ts) ?? 0) + value)
            }

            return Array.from(map.entries()).map(([timestamp, value]) => ({
                timestamp,
                value,
            }))
        }

        // compare
        const meterIdSet = new Set(meterIdsToShow)
        const map = new Map<string, IntervalTableRow>()
        for (const entry of data) {
            const ts = entry.date
            const entryId = getEntryId(entry, display)
            const value = getValueForEntry(entry, reportSettings.dataType)
            if (
                !ts ||
                typeof entryId !== 'number' ||
                value === undefined ||
                !meterIdSet.has(entryId)
            ) {
                continue
            }

            const key = entryId.toString()
            const row = map.get(ts) ?? { timestamp: ts }
            row[key] = value
            map.set(ts, row)
        }

        return Array.from(map.values())
    }, [data, dataGrouping, display, meterIdsToShow, reportSettings.dataType])

    const [sorting, setSorting] = useState<SortingState>([])

    return (
        <IntervalTable
            data={rows}
            columns={columns}
            sorting={sorting}
            setSorting={setSorting}
        />
    )
}
